/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useState, useEffect } from 'react';
import { css } from 'aphrodite';
import { colors, mixins, LoadingSpinner } from '@blend/consumer-components';
import { useHistory } from 'react-router';
import { Modal } from '@blend/shared-react';
import { StyleSheet } from 'aphrodite';
import { useConsumerTheme } from '../../hooks/useConsumerTheme';

import { useRoom } from '../../hooks/useRoom';
import { participantAPI } from '../../api/participant';
import { TaskMessages } from '../../components/form/TaskMessages/TaskMessages';
import { Footer } from '../TasksPage/Footer/Footer';
import { AgentCard, Assignee } from '../../components/form/AgentCard/AgentCard';
import { Message } from '../../components/form/TaskMessages/messageTypes';
import { getConfig } from '../../utils/configs';
import { useRootSelector } from '../../hooks/useRootSelector';
import NPS from '../../components/form/Inputs/NPS/NPS';
import { FeedbackModal } from '../../components/ParticipantRoom/FeedbackModal/FeedbackModal';
import Confetti from '../../components/common/Confetti/Confetti';
import { RouterStates } from '../../definitions/RouterStates';
import { styles as modalStyles } from '../../components/common/Modals/styles';
import { RoomStatus } from '../../shared/constants/room';

export namespace ParticipantRoomCompletePage {
  export interface Props {}
}

const overlayStyleConfig = {
  backgroundColor: colors.catalyst,
};
const styles = StyleSheet.create({
  contentWrapper: {
    ...mixins.margin(40, 25, 40, 25),
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'column',
    justifyContent: 'space-between',
    position: 'relative',
  },
  headerText: {
    fontSize: 24,
    textAlign: 'center',
    fontWeight: 'bold',
    marginBottom: 15,
  },
  helperText: {
    fontSize: 16,
    lineHeight: '22px',
    marginBottom: 10,
    textAlign: 'left',
  },
  surveyWrapper: {
    ...mixins.margin(20, 50, 50, 50),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  agentCard: {
    width: '100%',
    position: 'relative',
    marginBottom: 30,
  },
  loadingWrapper: {
    ...mixins.margin(20, 0, 0, 0),
  },
});

// Currently we have two ways of creating task template, one is from the client and the other is from
// the server task templates. Ideally, this should be consistence and every task should be created
// from the backend. However, the current task engine requires a session to exist when creating a task.
// This task here is shown when participant are too early to join the room (no session is created yet).
// Thus, as a workaround, creating task within the frontend component here.
// TODO: Refactor this and have all the tasks live in backend
export const END_MESSAGE: Message[] = [
  {
    display: `If you have any questions, please contact your notary. You can now close this window.`,
    type: 'simple',
    headingType: 'secondaryHeading',
  },
];
const satisfactionQuestion = 'How would you rate your overall satisfaction with this online closing experience?';

export const ParticipantRoomCompletePage: React.FC<ParticipantRoomCompletePage.Props> = ({}) => {
  const history = useHistory();
  const isFeedbackCollectionEnabled = getConfig().isFeedbackCollectionEnabled;
  const { roomId } = useRoom();
  const { appConfig } = useConsumerTheme();
  const [assignee, setAssignee] = useState<Assignee | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [roomStatus, setRoomStatus] = useState<RoomStatus>();
  const [feedbackModalIsOpen, setFeedbackModalIsOpen] = useState<boolean>(false);
  const [isFeedbackModalSubmitting, setIsFeedbackModalSubmitting] = useState(false);
  const [satisfactionValue, setSatisfactionValue] = useState<number | null>(null);
  const callId = useRootSelector(reduxState => reduxState.callDetails?.id);
  const handleRatingChange = (value: number) => {
    setSatisfactionValue(value);
    setFeedbackModalIsOpen(true);
  };
  const shouldDisplayFeedbackCollection = isFeedbackCollectionEnabled && roomStatus === RoomStatus.COMPLETED;

  useEffect(() => {
    (async () => {
      const roomDetails = await participantAPI.rooms.fetchRoomDetail(roomId);
      setAssignee(roomDetails.assignee);
      setIsLoading(false);
      setRoomStatus(roomDetails.status);
    })();
  }, [roomId]);

  if (isLoading) {
    return (
      <div className={css(styles.loadingWrapper)}>
        <LoadingSpinner size={'lg'} color={appConfig.colors.action} />
      </div>
    );
  }

  return shouldDisplayFeedbackCollection ? (
    <Modal
      overlayStyleConfig={overlayStyleConfig}
      data-it="ParticipantRoomCompleteWithFeedbackPage"
      isOpen
      contentLabel="ParticipantFeedback"
      handleClose={() => {}}
    >
      <Confetti />
      <div className={css(styles.contentWrapper)}>
        {feedbackModalIsOpen && satisfactionValue !== null ? (
          <div>
            <FeedbackModal
              contentLabel="participant_feedback_modal"
              callId={callId}
              satisfactionScore={satisfactionValue}
              isOpen={feedbackModalIsOpen}
              onSubmitSuccess={() => history.push(RouterStates.ROOM_EXIT)}
              isSubmitting={isFeedbackModalSubmitting}
              setIsSubmitting={setIsFeedbackModalSubmitting}
            />
          </div>
        ) : (
          <div>
            <div className={css(styles.headerText)}>This appointment has ended</div>
            <div className={css(styles.surveyWrapper)}>
              <TaskMessages messages={END_MESSAGE} />
              {!!assignee && (
                <div className={css(styles.agentCard)}>
                  <AgentCard assignee={assignee} />
                </div>
              )}
              <div className={css(styles.helperText)}>{satisfactionQuestion}</div>
              <NPS ratingValue={null} onChange={handleRatingChange} />
            </div>
          </div>
        )}
      </div>
    </Modal>
  ) : (
    <Modal
      overlayStyleConfig={overlayStyleConfig}
      data-it="ParticipantRoomCompletePage"
      isOpen
      contentLabel="ParticipantThankYou"
      handleClose={() => {}}
    >
      <div className={css(styles.contentWrapper)} data-it="ParticipantRoomCompletePage">
        <div className={css(styles.surveyWrapper)}>
          <h1 className={css(modalStyles.heading)}>Thank you!</h1>
          <TaskMessages messages={END_MESSAGE} />
          {!!assignee && (
            <div className={css(styles.agentCard)}>
              <AgentCard className={css(styles.agentCard)} assignee={assignee} />
            </div>
          )}
        </div>
        <Footer />
      </div>
    </Modal>
  );
};
